import {
  Box,
  Center,
  Flex,
  Grid,
  Heading,
  Skeleton,
  Spinner,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useToast,
} from '@chakra-ui/react';
import { usePaginationAdmin } from '@diamond/shared/context';
import {
  BackofficeGetGroupPromoDetailsResponse,
  GroupedPromoItem,
} from '@diamond/shared/types';
import {
  Alert,
  Breadcrumbs,
  PageLoader,
  Pagination,
  Searchbox,
  showToast,
} from '@diamond/shared/ui';
import { AdminLayout, ErrorPage } from '@diamond/sol-admin/components';
import { useAuthStore } from '@diamond/sol-admin/features/auth';
import { useSearchbox } from '@diamond/sol-admin/hooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import cleanDeep from 'clean-deep';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Control, FieldValues, useForm } from 'react-hook-form';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  BACKOFFICE_GROUP_PROMO_DETAIL_QUERY_KEY,
  useGroupPromoDetails,
} from '../api';
import { useGroupPromoHighlightList } from '../api/get-group-promo-highlight-list';
import { useGroupPromoTableMechanism } from '../api/get-table-mechanism';
import { updateHighlightPromo } from '../api/update-highlight-promo';
import { PromoDetailForm } from '../components';
import {
  ProductHighlightInput,
  ProductHighlightInputRef,
} from '../components/product/product-highlight-input';
import DetailsMechanisme from '../components/promo-detail/details-mechanisme';
import ProductHighlight from '../components/promo-detail/product-highlight';
import PromoDetailTableMechanism from '../components/promo-detail/promo-detail-table-mechanism';

interface PromoHighlightInputFormValues {
  highlight_number: number;
}

const getInitialHighlightNumberForCustomerType = (
  currentCustomerType: string | undefined,
  highlightNumberData:
    | BackofficeGetGroupPromoDetailsResponse['product_higlight_number_type']
    | undefined
): number => {
  if (!currentCustomerType || !highlightNumberData) {
    return 0;
  }

  if (currentCustomerType === '01') {
    return highlightNumberData.customer_type_01 ?? 0;
  }

  if (currentCustomerType === '10') {
    return highlightNumberData.customer_type_10 ?? 0;
  }

  return 0;
};

export function PromoDetailPage() {
  const paramsUrl = useParams<{ promoId: string }>();
  const [tabIndex, setTabIndex] = useState<number>(0);
  const [urlSearchParams, setUrlSearchParams] = useSearchParams();

  const links = [
    {
      title: 'Beranda',
      link: '/',
    },
    {
      title: 'Promo Management',
      link: '/promo',
    },
    {
      title: 'Promo Management Detail',
      link: `/promo/${paramsUrl['promoId']}`,
    },
  ];

  const navigate = useNavigate();
  const { register, params: searchboxParams } = useSearchbox();

  const queryClient = useQueryClient();
  const token = useAuthStore((s) => s.access_token);
  const toast = useToast();

  const productHighlightInputRef = useRef<ProductHighlightInputRef>(null);

  const promo_id = paramsUrl['promoId'] || '';
  const customerType = urlSearchParams.get('customer_type') || undefined;

  const defaultValues = useMemo(
    () => ({
      page: '1',
      size: '20',
      query: '',
    }),
    []
  );

  const setParams = useCallback(
    (nextParams: Record<string, string | undefined>) => {
      const prevParams = {
        ...defaultValues,
        ...Object.fromEntries(urlSearchParams.entries()),
      };
      setUrlSearchParams(
        cleanDeep({ ...prevParams, ...nextParams }) as URLSearchParams
      );
    },
    [urlSearchParams, setUrlSearchParams, defaultValues]
  );

  const params = useMemo(() => {
    const rawParams = {
      ...defaultValues,
      ...Object.fromEntries(urlSearchParams.entries()),
    };
    return {
      page: Number(rawParams.page),
      size: Number(rawParams.size),
      query: rawParams.query || '',
    };
  }, [urlSearchParams, defaultValues]);

  useEffect(() => {
    if (searchboxParams.query !== params.query) {
      setParams({ page: '1', query: searchboxParams.query || undefined });
    }
  }, [searchboxParams.query, params.query, setParams]);

  const {
    data,
    isPending: isLoading,
    error,
    dataUpdatedAt,
  } = useGroupPromoDetails(promo_id);
  const groupId = data?.group_id || '';
  const isGroupIdValid = typeof groupId === 'string' && groupId.trim() !== '';

  const {
    data: productHighlightList,
    isPending: isLoadingProductHighlightList,
    error: productHighlightListError,
  } = useGroupPromoHighlightList(groupId, {
    enabled: isGroupIdValid && !!groupId,
  });

  const {
    data: dataListTableMechanism,
    isPending: isLoadingDataTableMechanism,
    error: errorDataTableMechanism,
    refetch: refetchDataTableMechanism,
  } = useGroupPromoTableMechanism(
    promo_id,
    params.page,
    params.size,
    params.query
  );

  const dataTableMechanism = dataListTableMechanism?.data;
  const pageCount = dataListTableMechanism?.pagination.pages || 1;

  const { nextPage, previousPage, lastPage, firstPage } = usePaginationAdmin(
    params.page,
    (value: number) => setParams({ page: String(value) }),
    refetchDataTableMechanism,
    pageCount
  );
  const initialHighlightNumber = getInitialHighlightNumberForCustomerType(
    customerType,
    data?.product_higlight_number_type
  );

  const { control } = useForm<PromoHighlightInputFormValues>({
    defaultValues: {
      highlight_number: initialHighlightNumber,
    },
    values: {
      highlight_number: initialHighlightNumber,
    },
  });

  const handleTabChange = (index: number) => {
    setTabIndex(index);
  };
  const promoId = data?.id || '';

  const handleUpdateHighlight = useMutation({
    mutationFn: async (newHighlightNumber: number) => {
      const payload = {
        product_highlight_number: newHighlightNumber,
        customer_type: customerType,
      };
      if (!promoId) throw new Error('Promo ID is missing');
      await updateHighlightPromo(token, promoId, payload);
    },
    onSuccess: () => {
      showToast(toast, 'success', 'Berhasil update Product Highlight');
      productHighlightInputRef.current?.resetEditingState();
      queryClient.invalidateQueries({
        queryKey: [BACKOFFICE_GROUP_PROMO_DETAIL_QUERY_KEY, promoId],
      });
    },
    onError: (err: AxiosError<{ message: string; statusCode: number }>) => {
      const statusCode = err.response?.data?.statusCode || err.response?.status;
      const message = err.response?.data?.message || err.message;

      let toastMessage = 'Gagal Update Product Highlight';
      if (statusCode === 422) {
        toastMessage = message || 'Melebihi Kuantitas';
      } else if (statusCode === 404) {
        toastMessage = message || 'Promo Tidak ditemukan';
      } else if (message) {
        toastMessage = message;
      }

      showToast(toast, 'error', toastMessage);
    },
  });

  const handleSaveHighlight = (value: number) => {
    handleUpdateHighlight.mutate(value);
  };

  if (!promo_id) {
    return (
      <ErrorPage
        message="Promo ID Tidak Valid"
        subtitle="Promo ID Tidak Valid"
        buttonTitle="Kembali ke halaman promo"
        action={() => navigate('/promo')}
      />
    );
  }

  if (isLoading) {
    return (
      <AdminLayout>
        <PageLoader />
      </AdminLayout>
    );
  }

  if (error || !data) {
    return (
      <ErrorPage
        message={error?.message || 'Gagal memuat data promo.'}
        subtitle={error?.subtitle}
        buttonTitle="Kembali ke halaman promo"
        action={() => navigate('/promo')}
      />
    );
  }
  if (productHighlightListError) {
    return (
      <ErrorPage
        message={
          productHighlightListError?.message || 'Gagal memuat data promo.'
        }
        subtitle={productHighlightListError.name}
        buttonTitle="Kembali ke halaman promo"
        action={() => navigate('/promo')}
      />
    );
  }
  const renderProductHighlight = () => {
    if (!isGroupIdValid) {
      return (
        <Alert
          status="error"
          description="Cannot load product highlights: Group ID is missing or invalid."
        />
      );
    }

    if (isLoadingProductHighlightList) {
      return <Skeleton />;
    }

    if (productHighlightList && productHighlightList.length > 0) {
      return <ProductHighlight data={productHighlightList} promoId={promoId} />;
    }

    return (
      <Alert
        status="warning"
        description="No product highlights found for this promo."
      />
    );
  };

  const getPromoMechanismName = (
    item: GroupedPromoItem | undefined
  ): string => {
    if (!item) {
      return 'Tidak Diketahui'; // Return a default or loading state if item is not available
    }

    // Check if it's Free Goods
    if ('product_free_good' in item) {
      const fg = item.product_free_good;
      if (fg.sequential_number_for_free_goods_scale > 0) {
        return 'Free Goods Bertingkat';
      }
      return 'Free Goods';
    }

    // Check if it's Discount
    if ('product_discount' in item) {
      const disc = item.product_discount;
      const isPercentage = disc.is_discount;
      const isProrata = disc.prorate;
      // Assuming calculation_number starts at 1 for non-tiered
      const isTiered = disc.calculation_number > 1;

      if (isTiered) {
        return isPercentage
          ? 'Diskon % Bertingkat'
          : 'Diskon Nominal Bertingkat';
      }
      if (isProrata) {
        // Note: Tiered takes precedence over prorata naming if both flags could be true for a tier
        return isPercentage ? 'Diskon % Prorata' : 'Diskon Nominal Prorata';
      }
      // Base discount types
      return isPercentage ? 'Diskon %' : 'Diskon Nominal';
    }

    // Fallback if the item structure doesn't match known types
    return 'Tidak Diketahui';
  };
  const mechanismItems = dataTableMechanism?.items;
  const promoMechanismName = getPromoMechanismName(mechanismItems?.[0]);
  return (
    <AdminLayout>
      <Box paddingBottom={4}>
        <Breadcrumbs links={links} useRouterDom />
      </Box>
      <Box py={4}>
        <Stack spacing={8} direction="row" justifyContent="space-between">
          <Text fontSize="2xl" fontWeight="bold">
            Promo Management Detail
          </Text>
        </Stack>
      </Box>

      <PromoDetailForm key={dataUpdatedAt} data={data} />

      <Flex direction="column" w="full" mb={6}>
        <Heading fontSize="xl" mb="4">
          Detail Promo
        </Heading>

        <Flex justifyContent="space-between" alignItems="center" w="full">
          <Flex direction="column" gap={4}>
            <Flex direction="row" gap={12}>
              <Flex alignItems="center" gap={2}>
                <Text fontWeight="medium">Kode Promo Grouping:</Text>
                <Text fontWeight="bold">{data.group_id || '-'}</Text>
              </Flex>

              <Flex alignItems="center" gap={2}>
                <Text fontWeight="medium">Jumlah kode:</Text>
                <Text fontWeight="bold">{data.unique_promo_code_total}</Text>
              </Flex>
            </Flex>
            <Flex alignItems="center" gap={2}>
              <Text fontWeight="medium">Promo Mekanisme:</Text>
              <Text fontWeight="bold">{promoMechanismName}</Text>
            </Flex>
            <DetailsMechanisme data={dataTableMechanism?.items || []} />
          </Flex>
          <Box ml="auto" maxW="400px" w="full">
            {tabIndex === 0 && (
              <Searchbox
                register={register}
                w="full"
                placeholder="Cari nama produk atau kode promo"
              />
            )}
          </Box>
        </Flex>
      </Flex>

      <Tabs w="full" index={tabIndex} onChange={handleTabChange} isLazy>
        <TabList>
          <Tab
            px={1}
            color="gray.75"
            fontWeight="medium"
            _selected={{
              color: 'black',
              fontWeight: 'bold',
              borderColor: '#B80000',
            }}
          >
            <Text fontWeight="medium">
              Kode & Mekanisme
              <Box as="span" ml={1}>
                ({data.promo_code_total || 0})
              </Box>
            </Text>
          </Tab>

          <Tab
            px={1}
            color="gray.75"
            fontWeight="medium"
            _selected={{
              color: 'black',
              fontWeight: 'bold',
              borderColor: '#B80000',
            }}
          >
            <Text fontWeight="medium">
              Produk Highlight
              <Box as="span" ml={1}>
                ({data.product_highlight_total || 0})
              </Box>
            </Text>
          </Tab>
        </TabList>

        <TabPanels border="none" padding={0}>
          <TabPanel border="none" px={0}>
            {errorDataTableMechanism && (
              <Alert
                status="error"
                description={errorDataTableMechanism.message}
              />
            )}
            {isLoadingDataTableMechanism ? (
              <Skeleton h="500px" w="full" />
            ) : (
              <PromoDetailTableMechanism
                data={dataTableMechanism?.items || []}
              />
            )}
            <Center my={8}>
              <Pagination
                currentPage={params.page}
                totalPage={pageCount}
                nextPage={nextPage}
                firstPage={firstPage}
                lastPage={lastPage}
                previousPage={previousPage}
              />
            </Center>
          </TabPanel>

          <TabPanel border="none" px={0}>
            <Grid templateColumns={{ base: '1fr', md: '4fr 1fr' }} gap={6}>
              {renderProductHighlight()}
              <Stack w="full">
                <Text fontWeight="medium" align="center">
                  Product Highlight Number
                </Text>
                {/* Loading for Change State */}
                {isLoading ? (
                  <Spinner size="xl" />
                ) : (
                  <ProductHighlightInput
                    ref={productHighlightInputRef}
                    control={control as unknown as Control<FieldValues>}
                    name="highlight_number"
                    updateMutation={handleUpdateHighlight}
                    onSaveHighlight={handleSaveHighlight}
                  />
                )}
              </Stack>
            </Grid>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </AdminLayout>
  );
}
